import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { HeartPulse, MapPin, Stethoscope, Clock, Users, LogIn, UserPlus, UserRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { api, apiError, imgUrl } from "@/lib/api";
import { todayAST, fmtDate, nowTimeAST } from "@/lib/date";
import { useAuth } from "@/context/AuthContext";
import { InsuranceChips } from "@/components/InsurancesEditor";
import DocumentFields, { validateDocument } from "@/components/DocumentFields";

const storeKey = (id) => `booking:${id}`;

export default function PublicBooking() {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const saved = JSON.parse(sessionStorage.getItem(storeKey(doctorId)) || "null");
  const [doctor, setDoctor] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState(saved?.date || todayAST());
  const [viewMonth, setViewMonth] = useState(new Date((saved?.date || todayAST()) + "T00:00:00"));
  const [availDays, setAvailDays] = useState(new Set());
  const [blocks, setBlocks] = useState([]);
  const [selBlock, setSelBlock] = useState(null);
  const [selSlot, setSelSlot] = useState(null);
  const [pending, setPending] = useState(saved);
  const [reason, setReason] = useState(saved?.reason || "");
  const [mode, setMode] = useState(null);
  const [guest, setGuest] = useState({ name: "", phone: "", email: "" });
  const [doc, setDoc] = useState({ type: "", number: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/doctors/${doctorId}`).then((r) => setDoctor(r.data)).catch(() => setNotFound(true));
  }, [doctorId]);

  useEffect(() => {
    if (user?.document_number) setDoc({ type: user.document_type, number: user.document_number });
  }, [user]);

  const loadDays = async (m) => {
    const month = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
    const { data } = await api.get(`/doctors/${doctorId}/available-days`, { params: { month } });
    setAvailDays(new Set(data));
  };
  useEffect(() => { loadDays(viewMonth); /* eslint-disable-next-line */ }, [doctorId]);

  useEffect(() => {
    (async () => {
      setSelBlock(null); setSelSlot(null);
      const { data } = await api.get(`/doctors/${doctorId}/slots`, { params: { date } });
      const list = date === todayAST() ? data.filter((b) => (b.start_time || "") > nowTimeAST()) : data;
      setBlocks(list);
      if (pending && pending.date === date) {
        const b = list.find((x) => x.id === pending.blockId);
        if (b && (b.modality !== "HORAS" || (b.available_slots || []).includes(pending.slot))) {
          setSelBlock(b); setSelSlot(pending.slot || null);
        }
        setPending(null);
        sessionStorage.removeItem(storeKey(doctorId));
      }
    })();
    // eslint-disable-next-line
  }, [date, doctorId]);

  const goAuth = (path) => {
    sessionStorage.setItem(storeKey(doctorId), JSON.stringify({ date, blockId: selBlock?.id, slot: selSlot, reason }));
    navigate(`${path}?next=${encodeURIComponent(`/agendar/${doctorId}`)}`);
  };

  const book = async () => {
    if (!selBlock) { toast.error("Selecciona un horario disponible"); return; }
    if (selBlock.modality === "HORAS" && !selSlot) { toast.error("Selecciona una hora"); return; }
    const isGuest = !user;
    if (isGuest && !guest.name.trim()) { toast.error("Ingresa tu nombre completo"); return; }
    const docErr = validateDocument(doc.type, doc.number);
    if (docErr) { toast.error(docErr); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/public/appointments", {
        doctor_id: doctorId, availability_id: selBlock.id,
        slot_time: selBlock.modality === "HORAS" ? selSlot : null, reason,
        document_type: doc.type, document_number: doc.number,
        ...(isGuest ? { name: guest.name.trim(), phone: guest.phone.trim(), email: guest.email.trim() || null } : {}),
      });
      setDone(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (notFound) return <Shell><p className="text-center font-bold text-slate-500" data-testid="booking-not-found">Este link de agenda no es válido.</p></Shell>;
  if (!doctor || loading) return <Shell><p className="text-center font-bold text-slate-400">Cargando…</p></Shell>;
  if (done) return <Shell><Done appt={done} user={user} /></Shell>;

  const p = doctor.profile || {};
  const isPatient = user?.role === "patient";

  return (
    <Shell>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 lg:col-span-1" data-testid="public-doctor-card">
          {(doctor.picture || p.photo_url) && <img src={imgUrl(doctor.picture || p.photo_url)} alt={doctor.name} className="h-20 w-20 rounded-full border border-[#E2E8F0] object-cover" />}
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900" data-testid="public-doctor-name">{doctor.name}</h1>
          <p className="flex items-center gap-1.5 text-sm font-bold text-[#0284C7]"><Stethoscope className="h-4 w-4" /> {p.specialty}</p>
          <div className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500"><MapPin className="h-4 w-4 text-slate-400" /> {p.center_name}</div>
          {p.price ? <div className="mt-2 text-lg font-extrabold text-slate-900">RD$ {p.price?.toLocaleString?.() ?? p.price}</div> : null}
          <div className="mt-4 border-t border-[#E2E8F0] pt-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Seguros aceptados</div>
            <InsuranceChips items={p.insurances} testid="public-insurance-list" />
          </div>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900">1. Elige tu horario</h2>
            <div className="mt-3 inline-block rounded-xl border border-[#E2E8F0] p-2" data-testid="public-calendar">
              <Calendar mode="single" selected={new Date(date + "T00:00:00")} onSelect={(d) => d && setDate(fmtDate(d))}
                month={viewMonth} onMonthChange={(m) => { setViewMonth(m); loadDays(m); }}
                disabled={(d) => fmtDate(d) < todayAST() || !availDays.has(fmtDate(d))} />
            </div>
            <BlockList blocks={blocks} selBlock={selBlock} selSlot={selSlot} onPick={(b, s) => { setSelBlock(b); setSelSlot(s); }} />
            <div className="mt-5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Motivo de la cita</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Describe brevemente el motivo de tu consulta…" className="mt-1.5 min-h-20 rounded-xl border-[#E2E8F0]" data-testid="public-reason-input" />
            </div>
          </div>

          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-testid="public-patient-step">
            <h2 className="text-xl font-extrabold tracking-tight text-slate-900">2. Tus datos</h2>
            {user && !isPatient && (
              <p className="mt-3 rounded-xl bg-amber-50 p-4 text-sm font-bold text-amber-700" data-testid="public-not-patient">
                Estás conectado como {user.role === "doctor" ? "médico" : user.role === "assistant" ? "asistente" : "administrador"}. Para agendar, abre este link con una cuenta de paciente o sin sesión.
              </p>
            )}
            {isPatient && (
              <div className="mt-3 space-y-4">
                <p className="text-sm font-bold text-slate-600" data-testid="public-logged-as">Agendando como <span className="text-slate-900">{user.name}</span></p>
                <DocumentFields type={doc.type} number={doc.number} onChange={setDoc} disabled={!!user.document_number} prefix="public-doc" />
              </div>
            )}
            {!user && (
              <>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <OptionBtn icon={LogIn} label="Iniciar sesión" onClick={() => goAuth("/login")} testid="public-opt-login" />
                  <OptionBtn icon={UserPlus} label="Crear cuenta" onClick={() => goAuth("/registro")} testid="public-opt-register" />
                  <OptionBtn icon={UserRound} label="Agendar sin cuenta" active={mode === "guest"} onClick={() => setMode("guest")} testid="public-opt-guest" />
                </div>
                {mode === "guest" && (
                  <div className="mt-5 space-y-4" data-testid="public-guest-form">
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nombre completo *</Label><Input value={guest.name} onChange={(e) => setGuest((g) => ({ ...g, name: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="public-guest-name" /></div>
                    <DocumentFields type={doc.type} number={doc.number} onChange={setDoc} prefix="public-doc" />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label><Input value={guest.phone} onChange={(e) => setGuest((g) => ({ ...g, phone: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="public-guest-phone" /></div>
                      <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Correo (opcional)</Label><Input type="email" value={guest.email} onChange={(e) => setGuest((g) => ({ ...g, email: e.target.value }))} className="mt-1.5 rounded-xl border-[#E2E8F0]" data-testid="public-guest-email" /></div>
                    </div>
                  </div>
                )}
              </>
            )}
            {(isPatient || (!user && mode === "guest")) && (
              <Button onClick={book} disabled={busy} className="mt-6 w-full rounded-full bg-[#0284C7] font-bold hover:bg-[#0369a1]" data-testid="public-confirm-btn">
                {busy ? "Agendando…" : "Confirmar cita"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-[#E2E8F0] bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-6 py-4">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0284C7] text-white"><HeartPulse className="h-4 w-4" strokeWidth={2.6} /></div>
          <span className="text-base font-extrabold tracking-tight text-slate-900">SaludCitaRD</span>
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-6 py-8">{children}</div>
    </div>
  );
}

function OptionBtn({ icon: Icon, label, onClick, active, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-bold transition-colors duration-200 ${active ? "border-[#0284C7] bg-sky-50 text-[#0284C7]" : "border-[#E2E8F0] text-slate-600 hover:border-[#0284C7]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function BlockList({ blocks, selBlock, selSlot, onPick }) {
  return (
    <div className="mt-5 space-y-3">
      {blocks.length === 0 && <p className="rounded-xl border border-dashed border-[#E2E8F0] p-6 text-center text-sm font-bold text-slate-400" data-testid="public-no-slots">Sin disponibilidad para esta fecha.</p>}
      {blocks.map((b) => (
        <div key={b.id} className={`rounded-xl border p-4 ${selBlock?.id === b.id ? "border-[#0284C7] bg-sky-50/40" : "border-[#E2E8F0]"}`} data-testid={`public-block-${b.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
              {b.modality === "HORAS" ? <Clock className="h-4 w-4 text-[#0284C7]" /> : <Users className="h-4 w-4 text-[#0284C7]" />}
              {b.start_time}–{b.end_time} · {b.center_name}
            </div>
            <span className="rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs font-bold text-slate-500">{b.modality === "HORAS" ? "Hora fija" : `${b.remaining_cupos} cupos`}</span>
          </div>
          {b.modality === "HORAS" ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {(b.available_slots || []).length === 0 && <span className="text-xs font-bold text-slate-400">Sin horas libres</span>}
              {(b.available_slots || []).map((s) => (
                <button key={s} type="button" onClick={() => onPick(b, s)} data-testid={`public-slot-${b.id}-${s}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold ${selBlock?.id === b.id && selSlot === s ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600 hover:border-[#0284C7]"}`}>{s}</button>
              ))}
            </div>
          ) : (
            <button type="button" onClick={() => onPick(b, null)} disabled={b.remaining_cupos <= 0} data-testid={`public-cupo-${b.id}`}
              className={`mt-3 rounded-full border px-4 py-1.5 text-xs font-bold disabled:opacity-40 ${selBlock?.id === b.id ? "border-[#0284C7] bg-[#0284C7] text-white" : "border-[#E2E8F0] text-slate-600"}`}>
              {selBlock?.id === b.id ? "Seleccionado" : "Tomar cupo por llegada"}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function Done({ appt, user }) {
  return (
    <div className="mx-auto max-w-lg rounded-2xl border border-emerald-200 bg-white p-8 text-center" data-testid="public-booking-done">
      <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
      <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900">¡Cita agendada!</h1>
      <p className="mt-2 text-sm font-semibold text-slate-500">
        {appt.doctor_name} · {appt.date}{appt.slot_time ? ` · ${appt.slot_time}` : " · por orden de llegada"} · {appt.center}
      </p>
      {user?.role === "patient" ? (
        <Link to="/mis-citas" className="mt-6 inline-block rounded-full bg-[#0284C7] px-6 py-2.5 text-sm font-bold text-white" data-testid="public-go-my-appts">Ver mis citas</Link>
      ) : (
        <p className="mt-4 text-xs font-semibold text-slate-400">Si luego creas una cuenta con el mismo documento, verás esta cita en tu historial.</p>
      )}
    </div>
  );
}
